import { useQuery } from "@apollo/client/react";
import {
  Card,
  Center,
  Code,
  CopyButton,
  Group,
  Indicator,
  Loader,
  Progress,
  alpha,
  rem,
  SimpleGrid,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconArrowRight,
  IconCheck,
  IconClipboard,
  IconCode,
  IconExternalLink,
  IconTerminal2,
} from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { Link } from "react-router-dom";
import { useCurrentAccount } from "../../../business/CurrentAccount";
import { getProgressColor } from "../../../business/limits.hook";
import { ServiceItems } from "../ServiceLinks";
import "../styles/animations.css";

const ANALYTICS_QUERY = graphql(`
  query analytics {
    presenceAnalytics {
      anonymousSessions
      authenticatedSessions
      totalSessions
    }

    organizationLimits(limits: [REGISTERED_USERS]) {
      limits {
        id
        current
        max
      }
    }
  }
`);

const useStyles = createStyles((theme) => ({
  root: {
    maxWidth: rem(1200),
    margin: "0 auto",
    padding: `${rem(32)} ${rem(16)}`,
  },

  // Hero section
  hero: {
    position: "relative",
    borderRadius: theme.radius.lg,
    padding: `${rem(40)} ${rem(32)}`,
    marginBottom: rem(32),
    background: `linear-gradient(135deg, ${theme.colors.dark[7]} 0%, ${theme.colors.dark[8]} 100%)`,
    border: `1px solid ${theme.colors.dark[5]}`,
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      width: "40%",
      height: "100%",
      background: `radial-gradient(ellipse at 80% 50%, ${alpha(theme.colors.cyan[7], 0.08)} 0%, transparent 70%)`,
      pointerEvents: "none",
    },
  },
  heroGreeting: {
    fontSize: rem(14),
    fontWeight: 500,
    color: theme.colors.cyan[4],
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: rem(4),
  },
  heroTitle: {
    fontSize: rem(32),
    fontWeight: 700,
    color: theme.white,
    marginBottom: rem(8),
  },
  heroSubtitle: {
    fontSize: rem(15),
    color: theme.colors.dark[2],
    maxWidth: rem(500),
    lineHeight: 1.6,
  },

  // Stat cards row
  statsRow: {
    marginBottom: rem(32),
  },
  statCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.lg,
    padding: rem(20),
    transition: "border-color 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
    },
  },
  statLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    color: theme.colors.dark[2],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  statValue: {
    fontSize: rem(28),
    fontWeight: 700,
    lineHeight: 1.1,
    color: theme.white,
  },
  statSub: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },

  // Section
  sectionTitle: {
    fontSize: rem(18),
    fontWeight: 600,
    color: theme.colors.dark[0],
    marginBottom: rem(16),
  },
  sectionSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[3],
    marginBottom: rem(16),
    marginTop: rem(-12),
  },

  // Service cards
  serviceCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.lg,
    padding: rem(20),
    display: "flex",
    flexDirection: "column",
    gap: rem(12),
    transition: "all 200ms ease",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.colors.cyan[7],
      backgroundColor: theme.colors.dark[6],
      transform: "translateY(-2px)",
      boxShadow: `0 8px 24px ${alpha(theme.colors.dark[9], 0.4)}`,
    },
  },
  serviceCardDisabled: {
    opacity: 0.4,
    pointerEvents: "none" as const,
  },
  serviceIconWrap: {
    width: rem(40),
    height: rem(40),
    borderRadius: theme.radius.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  serviceName: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.white,
  },
  serviceDesc: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[2],
    lineHeight: 1.5,
  },
  serviceArrow: {
    color: theme.colors.dark[4],
    transition: "transform 200ms ease, color 200ms ease",
    "a:hover &, button:hover &": {
      transform: "translateX(3px)",
      color: theme.colors.cyan[4],
    },
  },

  // Quick start / API section
  apiSection: {
    marginTop: rem(32),
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.lg,
    padding: rem(28),
  },
  apiSectionTitle: {
    fontSize: rem(16),
    fontWeight: 600,
    color: theme.white,
    display: "flex",
    alignItems: "center",
    gap: rem(8),
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: rem(16),
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[1],
    overflowX: "auto",
    position: "relative",
  },
  codeComment: {
    color: theme.colors.dark[3],
  },
  codeKeyword: {
    color: theme.colors.cyan[4],
  },
  codeString: {
    color: theme.colors.green[4],
  },
  copyBtn: {
    position: "absolute",
    top: rem(8),
    right: rem(8),
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.sm,
    padding: rem(6),
    cursor: "pointer",
    transition: "all 150ms ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: theme.colors.dark[5],
    },
  },
  resourceLink: {
    display: "flex",
    alignItems: "center",
    gap: rem(8),
    padding: `${rem(10)} ${rem(14)}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    textDecoration: "none",
    transition: "all 150ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
      backgroundColor: theme.colors.dark[6],
    },
  },
  resourceLinkText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[1],
  },
  resourceLinkSub: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
}));

const services = ServiceItems;

const serviceColorMap: Record<string, string> = {
  purple: "rgba(168, 85, 247, 0.15)",
  amber: "rgba(245, 158, 11, 0.15)",
  cyan: "rgba(56, 190, 201, 0.15)",
  green: "rgba(16, 185, 129, 0.15)",
  rose: "rgba(244, 63, 94, 0.15)",
  dark: "rgba(255, 255, 255, 0.06)",
};

const serviceIconColorMap: Record<string, string> = {
  purple: "#a855f7",
  amber: "#f59e0b",
  cyan: "#38bec9",
  green: "#10b981",
  rose: "#f43f5e",
  dark: "#909296",
};

const CURL_SNIPPET = `curl -X POST \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "organization-id: YOUR_ORG_ID" \\
  -d '{"query": "{ tournaments { nodes { id title status } } }"}' \\
  https://api.warrior.well-played.gg/graphql`;

export const Dashboard = () => {
  const { classes, cx } = useStyles();
  const { account } = useCurrentAccount();
  const { data: analytics, loading: analyticsLoading } = useQuery<any>(
    ANALYTICS_QUERY,
    { pollInterval: 30000 },
  );

  const userEmail = String(account?.getMyAccount?.email ?? "");
  const userName = userEmail ? userEmail.split("@")[0] : "";

  const usersLimit = analytics?.organizationLimits?.limits[0];
  const usersCurrent = usersLimit?.current ?? 0;
  const usersMax = usersLimit?.max ?? 0;
  const usersPercent =
    usersMax > 0 ? Math.round((usersCurrent / usersMax) * 100) : 0;
  const usersColor = getProgressColor(usersPercent);

  const totalOnline = analytics?.presenceAnalytics?.totalSessions ?? 0;
  const authOnline = analytics?.presenceAnalytics?.authenticatedSessions ?? 0;
  const anonOnline = analytics?.presenceAnalytics?.anonymousSessions ?? 0;

  if (analyticsLoading && !analytics) {
    return (
      <Center py={80}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <div className={cx(classes.root, "wp-fade-in")}>
      {/* Hero */}
      <div className={classes.hero}>
        <Text className={classes.heroGreeting}>Welcome back</Text>
        <Title className={classes.heroTitle} order={1}>
          {userName || "Dashboard"}
        </Title>
        <Text className={classes.heroSubtitle}>
          Manage your gaming platform from one place. Build tournaments, track
          players, run events, and ship features with the WellPlayed API.
        </Text>
      </div>

      {/* Live stats */}
      <SimpleGrid
        cols={{ base: 1, sm: 3 }}
        spacing="md"
        className={classes.statsRow}
      >
        <div className={classes.statCard}>
          <Text className={classes.statLabel}>Registered users</Text>
          <Group gap="xs" align="baseline" mt={4}>
            <Text className={classes.statValue}>
              {usersCurrent.toLocaleString()}
            </Text>
            <Text className={classes.statSub}>
              / {usersMax.toLocaleString()}
            </Text>
          </Group>
          <Progress
            value={usersPercent}
            color={usersColor}
            size="sm"
            radius="xl"
            mt={8}
          />
          <Text className={classes.statSub} mt={4} c={`${usersColor}.4`}>
            {usersPercent}% of plan limit
          </Text>
        </div>

        <div className={classes.statCard}>
          <Text className={classes.statLabel}>Online now</Text>
          <Group gap="xs" align="baseline" mt={4}>
            <Indicator
              processing
              color="green"
              size={10}
              offset={-4}
              position="middle-end"
            >
              <Text className={classes.statValue}>{totalOnline}</Text>
            </Indicator>
          </Group>
          <Text className={classes.statSub} mt={4}>
            {authOnline} authenticated, {anonOnline} anonymous
          </Text>
        </div>

        <div className={classes.statCard}>
          <Text className={classes.statLabel}>API endpoint</Text>
          <Group gap="xs" align="baseline" mt={4}>
            <Indicator color="green" size={8} offset={-4} position="middle-end">
              <Text size="sm" fw={600} c="green.4">
                Operational
              </Text>
            </Indicator>
          </Group>
          <Text className={classes.statSub} mt={4}>
            api.warrior.well-played.gg
          </Text>
        </div>
      </SimpleGrid>

      {/* Services */}
      <Text className={classes.sectionTitle}>Services</Text>
      <Text className={classes.sectionSubtitle}>
        Jump into any service to manage your platform
      </Text>

      <SimpleGrid cols={{ base: 1, xs: 3, md: 3 }} spacing="md">
        {services.map((service) => (
          <UnstyledButton
            key={service.path}
            component={Link}
            to={service.path}
            className={classes.serviceCard}
          >
            <Group gap="md" wrap="nowrap">
              <div
                className={classes.serviceIconWrap}
                style={{
                  backgroundColor: serviceColorMap[service.color ?? "dark"],
                }}
              >
                <service.icon
                  size={20}
                  stroke={1.5}
                  color={serviceIconColorMap[service.color ?? "dark"]}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text className={classes.serviceName}>{service.title}</Text>
                <Text className={classes.serviceDesc}>
                  {service.description}
                </Text>
              </div>
              <IconArrowRight size={16} className={classes.serviceArrow} />
            </Group>
          </UnstyledButton>
        ))}
      </SimpleGrid>

      {/* Quick start / API */}
      <div className={classes.apiSection}>
        <Group gap="md" mb="lg">
          <ThemeIcon variant="light" color="cyan" size="lg" radius="md">
            <IconTerminal2 size={18} />
          </ThemeIcon>
          <div>
            <Text className={classes.apiSectionTitle}>Quick start</Text>
            <Text size="xs" c="dimmed">
              WellPlayed exposes a GraphQL API. Send a query to get started.
            </Text>
          </div>
        </Group>

        <div className={classes.codeBlock}>
          <CopyButton value={CURL_SNIPPET}>
            {({ copied, copy }) => (
              <Tooltip
                label={copied ? "Copied" : "Copy"}
                withArrow
                position="left"
              >
                <UnstyledButton
                  className={classes.copyBtn}
                  onClick={copy}
                  aria-label={copied ? "Copied" : "Copy snippet"}
                >
                  {copied ? (
                    <IconCheck size={14} color="#38bec9" />
                  ) : (
                    <IconClipboard size={14} color="#909296" />
                  )}
                </UnstyledButton>
              </Tooltip>
            )}
          </CopyButton>
          <div>
            <span className={classes.codeKeyword}>curl</span> -X POST \
          </div>
          <div>
            {"  "}-H{" "}
            <span className={classes.codeString}>
              "Content-Type: application/json"
            </span>{" "}
            \
          </div>
          <div>
            {"  "}-H{" "}
            <span className={classes.codeString}>
              "Authorization: Bearer YOUR_ACCESS_TOKEN"
            </span>{" "}
            \
          </div>
          <div>
            {"  "}-H{" "}
            <span className={classes.codeString}>
              "organization-id: YOUR_ORG_ID"
            </span>{" "}
            \
          </div>
          <div>
            {"  "}-d{" "}
            <span className={classes.codeString}>
              {"'"}
              {`{"query": "{ tournaments { nodes { id title status } } }"}`}
              {"'"}
            </span>{" "}
            \
          </div>
          <div>
            {"  "}
            <span className={classes.codeString}>
              https://api.warrior.well-played.gg/graphql
            </span>
          </div>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mt="lg">
          <a
            href="https://api.warrior.well-played.gg/graphql"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resourceLink}
          >
            <IconCode size={16} color="#38bec9" />
            <div>
              <Text className={classes.resourceLinkText}>API Playground</Text>
              <Text className={classes.resourceLinkSub}>
                Explore the schema interactively
              </Text>
            </div>
            <IconExternalLink
              size={14}
              color="#5C5F66"
              style={{ marginLeft: "auto" }}
            />
          </a>

          <a
            href="https://www.npmjs.com/package/@well-played.gg/typescript-sdk"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resourceLink}
          >
            <IconCode size={16} color="#f59e0b" />
            <div>
              <Text className={classes.resourceLinkText}>TypeScript SDK</Text>
              <Text className={classes.resourceLinkSub}>
                Official typed SDK for any TS project
              </Text>
            </div>
            <IconExternalLink
              size={14}
              color="#5C5F66"
              style={{ marginLeft: "auto" }}
            />
          </a>

          <a
            href="https://www.npmjs.com/package/@well-played.gg/react-sdk"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resourceLink}
          >
            <IconCode size={16} color="#a855f7" />
            <div>
              <Text className={classes.resourceLinkText}>React SDK</Text>
              <Text className={classes.resourceLinkSub}>
                React hooks and components
              </Text>
            </div>
            <IconExternalLink
              size={14}
              color="#5C5F66"
              style={{ marginLeft: "auto" }}
            />
          </a>

          <a
            href="https://the-guild.dev/graphql/codegen"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resourceLink}
          >
            <IconCode size={16} color="#10b981" />
            <div>
              <Text className={classes.resourceLinkText}>GraphQL Codegen</Text>
              <Text className={classes.resourceLinkSub}>
                Generate typed SDK from schema
              </Text>
            </div>
            <IconExternalLink
              size={14}
              color="#5C5F66"
              style={{ marginLeft: "auto" }}
            />
          </a>
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mt="lg">
          <Card
            p="md"
            radius="md"
            style={(theme) => ({
              backgroundColor: theme.colors.dark[8],
              border: `1px solid ${theme.colors.dark[5]}`,
            })}
          >
            <Group gap="xs" mb="xs">
              <Code>organization-id</Code>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                Required
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.6}>
              Set this header to the ID of the Organization you want to interact
              with. Find yours under{" "}
              <Text component={Link} to="/organizations" c="cyan.4" inherit>
                Organizations
              </Text>
              .
            </Text>
          </Card>

          <Card
            p="md"
            radius="md"
            style={(theme) => ({
              backgroundColor: theme.colors.dark[8],
              border: `1px solid ${theme.colors.dark[5]}`,
            })}
          >
            <Group gap="xs" mb="xs">
              <Code>Authorization</Code>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                Optional
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.6}>
              Pass a Bearer token to authenticate requests as a user. Format:{" "}
              <Code>Bearer {"<access_token>"}</Code>. Generate tokens via the{" "}
              <Text component={Link} to="/players" c="cyan.4" inherit>
                Players
              </Text>{" "}
              identity provider or the{" "}
              <Text component={Link} to="/apps" c="cyan.4" inherit>
                Apps
              </Text>{" "}
              service.
            </Text>
          </Card>
        </SimpleGrid>
      </div>
    </div>
  );
};
